import { SimulationRequestJson } from "../../business/ApiJsonTypes";
import {InstalledFanDatasheet} from "../../business/InstalledFanDatasheet";

export interface AssemblyPrecisePredefinedCriteria {
    adjustableImpellerId: string; // precise = we already know which exact impeller we work with

    impellerDiameter: number;
    bladeManufacturing: "standard" | "assg";
    preferredConstructionMaterialCode: string;
    preferredFastenersMaterialCode: string;
    // In assembly, mounbting position will be populated only if basically all the details are defined (horizonta/vertical, clutch side etc.) as its use single select to define them
    fanMountingPositionName: string | null;

    shaftDiameter: number | null;
    shaftLength: number | null;
    expectedImpellerHeight: number | null;

    customShaftThreadPreference: { customScrewM: number; customDlugoscGwintuWCzopieWalu: number } | null;

    bladeAngle: number;
    rpm: number;
    powerAtWorkingPoint: number;
    totalPressureAtWorkingPoint: number;
    softStartPresent: boolean;
    realizeWithClutch: boolean;
}

export function createAssemblyPrecisePreselectionCriteriaFromKnownDatasheeet(
    simulationRequest: SimulationRequestJson,
    datasheet: InstalledFanDatasheet,
): AssemblyPrecisePredefinedCriteria {
    const fanFlowSpec = simulationRequest.fanFlowSpecification;
    const mechanicalPrefs = simulationRequest.mechanicalPreferences;
    const manufacturingSpec = simulationRequest.manufacturingSpecification;

    let fanMountingPositionName : string | null = "";
    if (mechanicalPrefs.fanMountingOrientationCode && mechanicalPrefs.airflowRelativeClutchMountingSideCode && mechanicalPrefs.preferredSupportAndClutchDiscSideCode) {
        const orientation = mechanicalPrefs.fanMountingOrientationCode === "VERTICAL" ? "V" : "H";
        const clutchSide = mechanicalPrefs.airflowRelativeClutchMountingSideCode === "CLUTCH_ON_INLET" ? "I" : "O";
        const supports = mechanicalPrefs.preferredSupportAndClutchDiscSideCode === "SUPPORTS_AND_CLUTCH_SAME_SIDE" ? "2" : "1";
        fanMountingPositionName = `${orientation}S${clutchSide}${supports}`;
    }

    return {
        impellerDiameter: fanFlowSpec.fanDiameters.impellerDiameter,
        bladeAngle: parseFloat((fanFlowSpec.bladeAngle).toFixed(1)),
        rpm: fanFlowSpec.rpm,
        shaftDiameter: mechanicalPrefs.shaftDiameter,
        shaftLength: mechanicalPrefs.shaftLength,
        customShaftThreadPreference: mechanicalPrefs.customShaftThreadPreference,
        expectedImpellerHeight: mechanicalPrefs.expectedImpellerHeight,
        softStartPresent: simulationRequest.softStartPresent,
        realizeWithClutch: simulationRequest.realizeWithClutch,
        bladeManufacturing: manufacturingSpec.bladeManufacturingTypeCode === "assg" ? "assg" : "standard",
        fanMountingPositionName: fanMountingPositionName,
        // Extract blade profile from adjustableImpellerId (assumes format contains profile info)
        powerAtWorkingPoint: parseFloat(datasheet.parameters.find( p => p.identifier === "workingPointInstallationShaftPowerKiloWatt")?.value!),
        preferredConstructionMaterialCode: simulationRequest.manufacturingSpecification.materialPreference.preferredConstructionMaterialCode,
        preferredFastenersMaterialCode: simulationRequest.manufacturingSpecification.materialPreference.preferredFastenersMaterialCode,
        totalPressureAtWorkingPoint: parseFloat(datasheet.parameters.find( p => p.identifier === "workingPointTotalPressure")?.value!),
        adjustableImpellerId: fanFlowSpec.adjustableImpellerId
    };
}

function extractBladeProfileFromAdjustableId(adjustableImpellerId: string): string | undefined {
    // Try to extract blade profile pattern like "(14)" or "(15)" from the impeller ID
    const profileMatch = adjustableImpellerId.match(/\((\d+)\)/);
    return profileMatch ? profileMatch[0] : undefined;
}

export function encodeAssemblyCriteriaToBase64(criteria: AssemblyPrecisePredefinedCriteria): string {
    return btoa(JSON.stringify(criteria));
}

export function decodeAssemblyCriteriaFromBase64(encoded: string): AssemblyPrecisePredefinedCriteria | null {
    try {
        return JSON.parse(atob(encoded));
    } catch (e) {
        console.error("Failed to decode assembly criteria:", e);
        return null;
    }
}